import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Slider } from "../components/Slider";
import { Book } from "../components/Book";
import { BookCarousel } from "../components/BookCarousel";
import { classesForBook, classRecordsBookParam } from "../data/examBlock";
import { BreadCrumb } from "../components/BreadCrumb";
import type { Book as BookType, classDataProps, Chapter, Record as RecordType } from "../types/common";
import type { EmblaOptionsType } from "embla-carousel";
import { getBooks, getChapters, getTeacherClasses, getClassRecords, updateClass, deleteClass, getStudents, updateStudent } from "../services/api";
import { filterHiddenChapters } from "../data/hiddenChapters";
import { ClassSettingModal } from "../components/ClassSettingModal";
import { CreateClassModal } from "../components/CreateClassModal";
import { bookNeedsActivation } from "../utils/classCreation";
import { StudentSettingModal } from "../components/StudentSettingModal";
import Swal from "sweetalert2";
import type { StudentProps } from "../types/common";
import { getUserData } from "../utils/userData";
import { buildClassJoinInstruction } from "../utils/classInvite";
import { buildBatchUploadNavParams } from "../utils/teacherReturnNav";
import { useBatchQuota } from "../hooks/useBatchQuota";
import { quotaLabel, quotaExhausted, DAILY_LIMIT_MESSAGE } from "../utils/batchQuota";
import TopicDesc from "../components/TopicDesc";

const BookPreview: React.FC = () => {
  const navigate = useNavigate();
  const [books, setBooks] = useState<BookType[]>([]);
  const [selectBookId, setSelectBookId] = useState<string>("");
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [showQuestionPopup, setShowQuestionPopup] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [chaptersWithRecords, setChaptersWithRecords] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isChaptersLoading, setIsChaptersLoading] = useState(false);
  const [isLoadingRecords, setIsLoadingRecords] = useState(true);
  const [viewMode, setViewMode] = useState<"preview" | "assign">("preview");
  const [showCreateClassPopup, setShowCreateClassPopup] = useState(false);
  const [createdClasses, setCreatedClasses] = useState<Array<{
    _id: string;
    className: string;
    classCode: string;
    bookID: string;
  }>>([]);
  const [copiedClassId, setCopiedClassId] = useState<string | null>(null);
  const [isClassSetOpen, setIsClassSetOpen] = useState(false);
  const [selectedClassForSetting, setSelectedClassForSetting] = useState<{
    _id: string;
    className: string;
    classCode: string;
    bookID: string;
  } | null>(null);
  const [isStudentModalOpen, setIsStudentModalOpen] = useState(false);
  const [studentsData, setStudentsData] = useState<StudentProps[]>([]);

  const userData = getUserData();
  const { sub, email, name } = userData;
  // 批次上傳每日剩餘次數（kanban 1000892，比照國中）
  const { quota: batchQuota, refresh: refreshBatchQuota } = useBatchQuota(sub);
  const batchQuotaLabel = quotaLabel(batchQuota);

  // 檢查用戶是否為教師身份
  useEffect(() => {
    const checkTeacherAccess = () => {
      const userDataString = localStorage.getItem("oidc.user:https://id.hle.com.tw:js");
      
      if (!userDataString) {
        navigate("/");
        return;
      }

      try {
        const userData = JSON.parse(userDataString);
        const userRole = userData.profile?.role || userData.profile?.['custom:role'] || "學生";
        
        if (userRole !== "老師") {
          navigate("/student-home");
        }
      } catch (error) {
        console.error("解析用戶資料失敗:", error);
        navigate("/");
      }
    };

    checkTeacherAccess();
  }, [navigate]);

  // 載入書籍資料（只在初次載入時執行）
  useEffect(() => {
    // 只有在用戶登入且有 sub 時才載入資料
    if (!sub) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    getBooks()
      .then((res) => {
        const booksData = Array.isArray(res.data) ? res.data : [];
        setBooks(booksData);
        if (booksData.length > 0) {
          setSelectBookId(booksData[0]._id);
        }
      })
      .catch((error) => {
        console.error("載入書籍失敗:", error);
        setBooks([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [sub]);


  // 載入教師班級記錄以檢查哪些章節有提交記錄
  useEffect(() => {
    if (!sub || !selectBookId) return;

    const loadClassRecords = async () => {
      setIsLoadingRecords(true);
      try {
        // 獲取教師的所有班級
        const teacherClassesRes = await getTeacherClasses(sub);
        const teacherClasses = Array.isArray(teacherClassesRes.data) ? teacherClassesRes.data : [];

        // 更新已建立的班級列表
        setCreatedClasses(teacherClasses);

        // 找到當前書籍對應的班級(學測/模考專區沿用陪練國寫班級,kanban 107204)
        const bookClasses = classesForBook(teacherClasses, selectBookId);

        if (bookClasses.length === 0) {
          setChaptersWithRecords(new Set());
          return;
        }

        // 收集所有有記錄的章節ID
        const chaptersSet = new Set<string>();

        // 獲取每個班級的記錄
        for (const cls of bookClasses) {
          try {
            const recordsRes = await getClassRecords(cls.classCode, classRecordsBookParam(selectBookId));
            const records = Array.isArray(recordsRes.data) ? recordsRes.data : [];

            // 將有記錄的章節ID加入集合
            records.forEach((record: RecordType) => {
              if (record.chapterID) {
                chaptersSet.add(record.chapterID);
              }
            });
          } catch (err) {
            console.error(`載入班級 ${cls.className} 的記錄失敗:`, err);
          }
        }

        setChaptersWithRecords(chaptersSet);
      } catch (error) {
        console.error("載入班級記錄失敗:", error);
        setChaptersWithRecords(new Set());
      } finally {
        setIsLoadingRecords(false);
      }
    };

    loadClassRecords();
  }, [sub, selectBookId]);

  // 載入章節資料
  useEffect(() => {
    if (selectBookId && sub) {
      setIsChaptersLoading(true);
      getChapters(selectBookId, sub)
        .then((res) => {
          setChapters(filterHiddenChapters(res.data || []));
        })
        .catch((err) => {
          console.error('獲取章節失敗:', err);
          setChapters([]);
        })
        .finally(() => {
          setIsChaptersLoading(false);
        });
    }
  }, [selectBookId, sub]);
  
  const SLIDE_COUNT = books.length;
  const SLIDES = Array.from(Array(SLIDE_COUNT).keys());
  const OPTIONS: EmblaOptionsType = { slidesToScroll: "auto" };
  
  // 處理預覽題目
  const handlePreviewQuestion = (chapter: Chapter) => {
    setSelectedChapter(chapter);
    setShowQuestionPopup(true);
  };

  // 上傳班級作文(批次上傳精靈,kanban 106957)。組參數邏輯抽成純函式
  // buildBatchUploadNavParams(src/utils/teacherReturnNav.ts)——chapterId 帶章節名稱、
  // chapterUid 帶章節 _id,兩者語意不同;抽出後才有測試釘住這個對映關係,避免未來
  // 改錯來源時沒有任何測試失敗、靜默退回 fallback 行為(kanban 106957 code review)。
  const handleBatchUpload = async (chapter: Chapter) => {
    // 次數用盡就不進精靈（kanban 1000892，文案同國中）；擋之前先重查一次，避免拿
    // 停留很久的過期數字擋人（計數國中高中共用、跨日）；讀不到次數不擋，交給後端
    const fresh = quotaExhausted(batchQuota) ? await refreshBatchQuota() : batchQuota;
    if (quotaExhausted(fresh)) {
      Swal.fire({ icon: "warning", text: DAILY_LIMIT_MESSAGE, confirmButtonText: "確定" });
      return;
    }
    const params = new URLSearchParams(buildBatchUploadNavParams(selectBookId, chapter));
    navigate(`/batch-upload?${params.toString()}`);
  };

  // 處理檢視紀錄
  const handleViewRecords = (chapter: Chapter) => {
    // 使用 URL 參數傳遞書籍和章節資訊
    const params = new URLSearchParams({
      bookId: selectBookId,
      chapterId: chapter._id,
      chapterName: chapter.chapter
    });

    navigate(`/teacher/correcting-records?${params.toString()}`);
  };

  // 處理班級名稱變更
  const handleClassNameChange = async (newClassName: string) => {
    if (!selectedClassForSetting) return;

    try {
      await updateClass(
        selectedClassForSetting._id,
        sub,
        email,
        name,
        newClassName,
        selectedClassForSetting.classCode
      );

      // 更新本地狀態
      setCreatedClasses(prev =>
        prev.map(cls =>
          cls._id === selectedClassForSetting._id
            ? { ...cls, className: newClassName }
            : cls
        )
      );

      Swal.fire({
        icon: "success",
        title: "班級名稱變更成功",
        timer: 1500,
        showConfirmButton: false,
      });

      setIsClassSetOpen(false);
    } catch (error) {
      console.error("變更班級名稱失敗:", error);
      Swal.fire({
        icon: "error",
        title: "變更失敗",
        text: "請稍後再試",
      });
    }
  };

  // 開啟學生設定 Modal
  const handleOpenStudentModal = async () => {
    if (!selectedClassForSetting) return;

    try {
      const response = await getStudents(selectedClassForSetting.classCode);
      setStudentsData(Array.isArray(response.data) ? response.data : []);
      setIsStudentModalOpen(true);
    } catch (error) {
      console.error("獲取學生資料失敗:", error);
      Swal.fire({
        icon: "error",
        title: "載入失敗",
        text: "無法載入學生資料",
      });
    }
  };

  // 更新學生資料
  const handleUpdateStudent = async (
    student: StudentProps,
    updateData: { name: string; seatNumber: string }
  ) => {
    if (!selectedClassForSetting) return;

    try {
      await updateStudent(
        student._id,
        selectedClassForSetting.classCode,
        student.studentID,
        updateData.name,
        student.studentEmail,
        updateData.seatNumber,
        parseInt(updateData.seatNumber) || 0
      );

      // 更新本地狀態
      setStudentsData(prev =>
        prev.map(s =>
          s._id === student._id
            ? { ...s, studentName: updateData.name, studentSeatNo: updateData.seatNumber }
            : s
        )
      );

      Swal.fire({
        icon: "success",
        title: "更新成功",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("更新學生資料失敗:", error);
      Swal.fire({
        icon: "error",
        title: "更新失敗",
        text: "請稍後再試",
      });
    }
  };

  // 刪除班級
  const handleDeleteClass = async () => {
    if (!selectedClassForSetting) return;

    const result = await Swal.fire({
      title: "刪除班級確認",
      html: `
        <div style="text-align: center; line-height: 1.8;">
          <p>您確定要刪除此班級嗎？</p>
          <p>此動作無法復原，</p>
          <p>該班級所有的批改與測驗紀錄將永久移除，</p>
          <p>您將無法再檢視或下載任何相關資料。</p>
          <p style="margin-top: 16px;">為了確保您已了解風險，</p>
          <p>請勾選「確認執行刪除」以繼續。</p>
        </div>
      `,
      icon: "warning",
      input: "checkbox",
      inputPlaceholder: "確認執行刪除",
      showCancelButton: true,
      confirmButtonText: "確認刪除",
      cancelButtonText: "取消",
      confirmButtonColor: "#6e7881",
      reverseButtons: true,
      didOpen: () => {
        const confirmBtn = Swal.getConfirmButton();
        const checkbox = Swal.getInput();
        if (checkbox && confirmBtn) {
          checkbox.addEventListener("change", () => {
            if (checkbox.checked) {
              confirmBtn.style.backgroundColor = "#d33";
            } else {
              confirmBtn.style.backgroundColor = "#6e7881";
            }
          });
        }
      },
      inputValidator: (result) => {
        if (!result) {
          return "請勾選確認執行刪除才能繼續";
        }
        return null;
      },
    });

    if (result.isConfirmed) {
      try {
        await deleteClass(selectedClassForSetting.classCode);

        // 更新本地狀態
        setCreatedClasses(prev =>
          prev.filter(cls => cls._id !== selectedClassForSetting._id)
        );

        setIsClassSetOpen(false);
        setSelectedClassForSetting(null);

        Swal.fire({
          icon: "success",
          title: "班級已刪除",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("刪除班級失敗:", error);
        Swal.fire({
          icon: "error",
          title: "刪除失敗",
          text: "請稍後再試",
        });
      }
    }
  };

  // 如果未登入，顯示提示訊息
  if (!sub) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <Header />
        
        {/* 麵包屑導航 */}
        <div className="md:px-xl px-sm max-w-2xl mx-auto w-full">
          <div className="flex items-center py-4">
            <BreadCrumb breadCrumb="/teacher/book-preview" />
          </div>
          <hr className="text-black/20" />
        </div>
        
        <main className="flex-grow md:px-xl px-sm py-md max-w-2xl mx-auto w-full">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-700 mb-4">請先登入</h2>
              <p className="text-gray-500 mb-6">您需要以教師身份登入才能查看書籍預覽</p>
              <button 
                onClick={() => navigate("/")}
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                返回首頁
              </button>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      
      {/* 麵包屑導航 */}
      <div className="md:px-xl px-sm max-w-2xl mx-auto w-full">
        <div className="flex items-center py-4">
          <BreadCrumb breadCrumb="/teacher/book-preview" />
        </div>
        <hr className="text-black/20" />
      </div>
      
      <main className="flex-grow md:px-xl px-sm py-md max-w-2xl mx-auto w-full">
        <div className="flex flex-col gap-6 w-full">
          {/* 按鈕區 */}
          <div className="flex gap-2 sm:gap-4 mx-2">
            <button
              onClick={() => setViewMode("preview")}
              className={`flex-1 sm:flex-none px-3 py-2 sm:px-6 sm:py-3 text-sm sm:text-lg rounded-lg transition-colors border ${
                viewMode === "preview"
                  ? "bg-secondary text-white border-secondary"
                  : "bg-gray-100 text-[#2781E2] border-[#2781E2] hover:bg-gray-200"
              }`}
            >
              批改＆檢視紀錄
            </button>
            <button
              onClick={() => setViewMode("assign")}
              className={`flex-1 sm:flex-none px-3 py-2 sm:px-6 sm:py-3 text-sm sm:text-lg rounded-lg transition-colors border ${
                viewMode === "assign"
                  ? "bg-primary text-white border-primary"
                  : "bg-gray-100 text-[#2781E2] border-[#2781E2] hover:bg-gray-200"
              }`}
            >
              派出作文
            </button>
          </div>

          {/* 書籍輪播 */}
          <section className="mx-2">
            {isLoading ? (
              <div className="w-full flex flex-col items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                <span className="text-gray-500">載入書籍中...</span>
              </div>
            ) : (
              <>
                <Slider
                  slides={SLIDES}
                  options={OPTIONS}
                  booksData={books}
                  selectBookId={selectBookId}
                  setSelectBookId={setSelectBookId}
                />
                <BookCarousel>
                  {Array.isArray(books) && books.map((book: BookType) => {
                    if (!book || !book._id) return null;
                    return (
                      <Book
                        key={book._id}
                        book={book}
                        selectBookId={selectBookId}
                        setSelectBookId={setSelectBookId}
                      />
                    );
                  })}
                </BookCarousel>
              </>
            )}
          </section>

          {/* 操作提醒 - 僅在派出作文模式顯示 */}
          {viewMode === "assign" && (
            <div className="mx-2 px-4 py-3 bg-[#E8F4FC] border border-secondary rounded-lg flex items-center gap-2">
              <span className="text-lg flex-shrink-0">👉</span>
              <span className="text-primary">
                操作提醒：不同書籍對應不同邀請碼。請先切換至目標書籍，再建立班級或複製代碼，以免發送錯誤。
              </span>
            </div>
          )}

          {/* 題目表格 */}
          {viewMode === "preview" && (
          <section className="bg-white rounded-lg shadow-md p-3 md:p-4 overflow-visible">
            {/* 每日剩餘次數是每位老師一天一個數字，整頁只顯示一次（kanban 1000892，同國中） */}
            {batchQuotaLabel != null && (
              <div className="flex justify-end mb-2">
                <span className="text-xs border border-[#254D71] text-[#254D71] rounded px-2 py-0.5 whitespace-nowrap">
                  今日剩餘次數：{batchQuotaLabel}
                </span>
              </div>
            )}
            {isLoading || isChaptersLoading ? (
              <div className="p-6 text-center text-gray-500 flex flex-col items-center justify-center">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                <span>載入中...</span>
              </div>
            ) : chapters.length > 0 ? (
              <>
                {/* 桌機/平板：表格 */}
                <table className="hidden md:table w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left p-3 font-semibold text-gray-700">來源</th>
                      <th className="text-left p-3 font-semibold text-gray-700">題目</th>
                      <th className="text-left p-3 font-semibold text-gray-700">功能選項</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chapters.map((chapter) => (
                      <tr key={chapter._id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-3 text-gray-600">
                          {chapter.source}
                        </td>
                        <td className="p-3 text-gray-800">{chapter.chapter}</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handlePreviewQuestion(chapter)}
                              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            >
                              預覽題目
                            </button>
                            <button
                              onClick={() => handleBatchUpload(chapter)}
                              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            >
                              上傳班級作文
                            </button>
                            <button
                              onClick={() => handleViewRecords(chapter)}
                              disabled={isLoadingRecords || !chaptersWithRecords.has(chapter.chapter)}
                              className={`px-4 py-2 rounded transition-colors relative group ${
                                isLoadingRecords
                                  ? 'bg-blue-100 text-blue-400 cursor-wait'
                                  : chaptersWithRecords.has(chapter.chapter)
                                    ? 'bg-blue-500 text-white hover:bg-blue-600 cursor-pointer'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              {isLoadingRecords ? (
                                <span className="flex items-center gap-1.5">
                                  <span className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></span>
                                  載入中
                                </span>
                              ) : (
                                '檢視紀錄'
                              )}
                              {!isLoadingRecords && (
                                <span className="absolute top-1/2 left-full ml-1 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                                  {chaptersWithRecords.has(chapter.chapter) ? '已有繳交紀錄' : '尚無繳交紀錄'}
                                </span>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* 手機：卡片 */}
                <div className="md:hidden flex flex-col gap-3">
                  {chapters.map((chapter) => {
                    const hasRecord = chaptersWithRecords.has(chapter.chapter);
                    return (
                      <div
                        key={chapter._id}
                        className="border border-gray-200 rounded-md p-3 flex flex-col gap-2"
                      >
                        <div className="text-xs text-gray-500">{chapter.source}</div>
                        <div className="text-base text-gray-800 font-medium break-words">
                          {chapter.chapter}
                        </div>
                        <div className="flex gap-2 mt-1">
                          <button
                            onClick={() => handlePreviewQuestion(chapter)}
                            className="flex-1 px-3 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                          >
                            預覽題目
                          </button>
                          <button
                            onClick={() => handleBatchUpload(chapter)}
                            className="flex-1 px-3 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                          >
                            上傳班級作文
                          </button>
                          <button
                            onClick={() => handleViewRecords(chapter)}
                            disabled={isLoadingRecords || !hasRecord}
                            className={`flex-1 px-3 py-2 text-sm rounded transition-colors ${
                              isLoadingRecords
                                ? 'bg-blue-100 text-blue-400 cursor-wait'
                                : hasRecord
                                  ? 'bg-blue-500 text-white hover:bg-blue-600 cursor-pointer'
                                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            {isLoadingRecords ? '載入中' : hasRecord ? '檢視紀錄' : '尚無紀錄'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="p-6 text-center text-gray-500">
                {!sub ? '請先登入' : '無可用題目'}
              </div>
            )}
          </section>
          )}

          {viewMode === "assign" && (
            <section className="bg-white rounded-lg shadow-md p-3 md:p-4 overflow-visible">
              <div className="flex flex-wrap gap-3 md:gap-4">
                {/* 已建立的班級卡片 */}
                {createdClasses
                  .filter(cls => cls.bookID === selectBookId)
                  .map(cls => (
                    <div
                      key={cls._id}
                      className="relative bg-[#E8F4FC] border-2 border-[#5B9BD5] rounded-xl p-4 md:p-5 flex flex-col gap-3 md:gap-4 w-full sm:w-auto sm:min-w-[180px]"
                    >
                      {/* 班級設定按鈕 */}
                      <button
                        onClick={() => {
                          setSelectedClassForSetting(cls);
                          setIsClassSetOpen(true);
                        }}
                        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="班級設定"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                      <span className="text-gray-800 font-medium text-lg">{cls.className}</span>
                      <div className="relative">
                        <span className="absolute -top-2.5 left-3 bg-[#E8F4FC] px-1 text-sm text-gray-500">邀請碼</span>
                        <div className="flex items-center bg-white border border-gray-300 rounded-lg px-4 py-3">
                          <span className="text-[#254D71] font-medium text-lg flex-1">{cls.classCode}</span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(buildClassJoinInstruction(cls.classCode));
                              setCopiedClassId(cls._id);
                              setTimeout(() => setCopiedClassId(null), 2000);
                            }}
                            className="text-gray-400 hover:text-gray-600"
                            title="複製說明"
                          >
                            {copiedClassId === cls._id ? (
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                {/* 建立新班級卡片 */}
                <button
                  onClick={() => setShowCreateClassPopup(true)}
                  className="w-full sm:w-auto sm:min-w-[180px] bg-blue-50 border-2 border-dashed border-blue-400 rounded-xl p-4 md:p-5 flex flex-row sm:flex-col items-center justify-center gap-2 sm:gap-3 hover:bg-blue-100 transition-colors cursor-pointer"
                >
                  <span className="text-2xl sm:text-3xl text-[#254D71]">+</span>
                  <span className="text-[#254D71] font-medium text-base sm:text-lg">建立新班級</span>
                </button>
              </div>
            </section>
          )}
        </div>
      </main>

      {/* 建立新班級 Popup(抽出為共用元件,批次上傳精靈也使用;kanban 106957) */}
      {showCreateClassPopup && (
        <CreateClassModal
          bookId={selectBookId}
          needActivation={bookNeedsActivation(books, selectBookId)}
          teacherId={sub}
          teacherEmail={email}
          teacherName={name}
          existingClasses={classesForBook(createdClasses, selectBookId)}
          onCreated={(cls) => setCreatedClasses((prev) => [...prev, cls])}
          onClose={() => setShowCreateClassPopup(false)}
        />
      )}

      {/* 題目預覽 Popup */}
      {showQuestionPopup && selectedChapter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold text-gray-800">{selectedChapter.chapter}</h2>
              <button
                onClick={() => setShowQuestionPopup(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            
            {/* 題目內容 - 顯示章節描述和圖片 */}
            {selectedChapter.chapterDesc && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3">題目內容</h3>
                <div className="text-gray-700 text-sm leading-relaxed">
                  <TopicDesc
                    desc={selectedChapter.chapterDesc}
                    images={selectedChapter.images}
                    chapterName={selectedChapter.chapter}
                  />
                </div>
                
                {/* 顯示章節圖片 */}
                {selectedChapter.chapterDescUrl && (
                  <div className="mt-4">
                    <img
                      src={selectedChapter.chapterDescUrl}
                      alt={`${selectedChapter.chapter} - 章節圖片`}
                      className="w-full max-w-2xl mx-auto rounded-md border border-gray-200 shadow-sm"
                      loading="lazy"
                      onError={(e) => {
                        console.warn('章節圖片載入失敗:', selectedChapter.chapterDescUrl);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowQuestionPopup(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 班級設定 Modal */}
      <ClassSettingModal
        open={isClassSetOpen}
        onClose={() => setIsClassSetOpen(false)}
        className={selectedClassForSetting?.className}
        handleClassNameChange={handleClassNameChange}
        handleOpenStudentModal={handleOpenStudentModal}
        canEditStudent={true}
        handleDeleteClass={handleDeleteClass}
      />

      {/* 學生設定 Modal */}
      <StudentSettingModal
        open={isStudentModalOpen}
        onClose={() => setIsStudentModalOpen(false)}
        studentsData={studentsData}
        selectClass={selectedClassForSetting as classDataProps | null}
        handleUpdateStudent={handleUpdateStudent}
      />

      <Footer />
    </div>
  );
};

export default BookPreview;
